import {
  Box,
  Checkbox,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon, PlusCircleOutlineIcon } from '@atgfr8/icons';
import { BasicTable, Button } from '@components';
import {
  customerDetailsStore,
  MetaData,
  setCustomerDetailsStore,
} from '@store/customers/customerDetails';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { Index, Show } from 'solid-js';

type CustomFieldsProps = {
  modalId: string;
  handleClose?: () => void;
};

const CustomFields = (props: CustomFieldsProps) => {
  const handleAddRow = () => {
    const defaultMetadata: MetaData = {
      objectId: customerDetailsStore.customer.id ?? '',
      operationType: 'Insert',
      propertyName: '',
      value: null,
      active: true,
      defaultValue: null,
      isReadOnly: false,
      showOnBOL: false,
      showOnRateCon: false,
      showOnInvoice: true,
    };
    setCustomerDetailsStore('customer', 'metadata', [
      ...customerDetailsStore.customer.metadata,
      defaultMetadata,
    ]);
  };

  const handleDeleteRow = (index: number) => {
    const metaData = [...customerDetailsStore.customer.metadata];
    if (metaData[index].operationType === 'Insert') {
      metaData.splice(index, 1);
    } else {
      metaData[index] = {
        ...metaData[index],
        operationType: 'Delete',
      };
    }
    setCustomerDetailsStore('customer', 'metadata', metaData);
  };

  const handleChange = (
    index: number,
    key: string,
    value: string | number | boolean,
  ) => {
    const localMeta = [...customerDetailsStore.customer.metadata];
    if (localMeta[index].operationType !== 'Insert') {
      localMeta[index] = {
        ...localMeta[index],
        [key]: value,
        operationType: 'Update',
      };
    } else {
      localMeta[index] = {
        ...localMeta[index],
        [key]: value,
      };
    }
    setCustomerDetailsStore('customer', 'metadata', localMeta);
  };

  const columns = [
    {
      name: 'Field Name',
      key: 'fieldName',
    },
    {
      name: 'Default Value',
      key: 'defaultValue',
    },
    {
      name: 'Is Read Only',
      key: 'isReadOnly',
    },
    {
      name: 'Show On BOL',
      key: 'showOnBOL',
    },
    {
      name: 'Show On Rate Con',
      key: 'showOnRateCon',
    },
    {
      name: 'Show On Invoice',
      key: 'showOnInvoice',
    },
    {
      name: '',
      key: 'action',
    },
  ];

  return (
    <Box class="overflow-hidden">
      <Box class="bg-[#d9edf7] p-[15px] rounded-sm">
        <Typography
          class="text-[#31708f] text-base! font-normal!"
          variant="h6"
          component="h2"
        >
          Add fields here that will appear on your orders for this customer.
          Example: Quote Id
        </Typography>
      </Box>
      <BasicTable<MetaData> columns={columns}>
        <Index each={customerDetailsStore.customer.metadata}>
          {(rowData, index) => (
            <Show when={rowData().operationType !== 'Delete'}>
              <TableRow>
                <TableCell>
                  <TextField
                    value={rowData().propertyName}
                    onChange={(val: string) =>
                      handleChange(index, 'propertyName', val)
                    }
                    label=""
                    size="sm"
                    dataTestId={`custom-fields-property-name-textInput-${index}`}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={
                      rowData().defaultValue !== null
                        ? (rowData().defaultValue as string)
                        : ''
                    }
                    onChange={(val: string) =>
                      handleChange(index, 'defaultValue', val)
                    }
                    label=""
                    size="sm"
                    dataTestId={`custom-fields-default-value-textInput-${index}`}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                    label="Is Read Only"
                    checked={
                      rowData().isReadOnly !== null
                        ? (rowData().isReadOnly as boolean)
                        : false
                    }
                    onChange={(e) =>
                      handleChange(index, 'isReadOnly', e.valueOf())
                    }
                    dataTestId={`custom-fields-is-read-only-checkboxInput-${index}`}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                    label="Show on BOL"
                    checked={
                      rowData().showOnBOL !== null
                        ? (rowData().showOnBOL as boolean)
                        : false
                    }
                    onChange={(e) =>
                      handleChange(index, 'showOnBOL', e.valueOf())
                    }
                    dataTestId={`custom-fields-show-on-bol-checkboxInput-${index}`}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                    label="Show on Rate Con"
                    checked={
                      rowData().showOnRateCon !== null
                        ? (rowData().showOnRateCon as boolean)
                        : false
                    }
                    onChange={(e) =>
                      handleChange(index, 'showOnRateCon', e.valueOf())
                    }
                    dataTestId={`custom-fields-show-on-rate-con-checkboxInput-${index}`}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                    label="Show on Invoice"
                    checked={
                      rowData().showOnInvoice !== null
                        ? (rowData().showOnInvoice as boolean)
                        : true
                    }
                    onChange={(e) =>
                      handleChange(index, 'showOnInvoice', e.valueOf())
                    }
                    dataTestId={`custom-fields-show-on-invoice-checkboxInput-${index}`}
                  />
                </TableCell>
                <TableCell class="w-[20px]">
                  <DeleteIcon
                    class="cursor-pointer"
                    onClick={() => {
                      handleDeleteRow(index);
                    }}
                    color="#b00020"
                  />
                </TableCell>
              </TableRow>
            </Show>
          )}
        </Index>
      </BasicTable>

      <Stack direction="row" gap={1} mt={2} class="justify-between">
        <Box>
          <Button
            variant="text"
            label="Add New Field"
            startIcon={<PlusCircleOutlineIcon size={20} />}
            onClick={handleAddRow}
            dataTestId={formatDataTestId('Add New Field', 'button')}
          />
        </Box>
        <Stack direction="row" gap={1}>
          <Button
            class="flex-1"
            variant="text"
            label="Cancel"
            onClick={props.handleClose}
            dataTestId={formatDataTestId('Cancel', 'button')}
          />

          <Button
            variant="contained"
            class="flex-1"
            label="Done"
            onClick={() => props.handleClose && props.handleClose()}
            dataTestId={formatDataTestId('Done', 'button')}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default CustomFields;
